import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link2,
  Quote,
  Code,
  Undo,
  Redo,
} from "lucide-react";

const Button = ({
  children,
  variant = "default",
  onClick,
  disabled,
  className = "",
  ...props
}) => {
  const baseClasses =
    "px-4 py-2 rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500";
  const variants = {
    default: "bg-gray-100 hover:bg-gray-200 text-gray-900",
    primary: "bg-purple-600 hover:bg-purple-700 text-white",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 text-gray-900",
  };

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${
        disabled ? "opacity-50 cursor-not-allowed" : ""
      } ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Start typing...",
}) {
  const editorRef = useRef(null);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [activeFormats, setActiveFormats] = useState({});

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value;
    }
  }, [value]);

  // Update active formats based on cursor position
  const updateActiveFormats = useCallback(() => {
    if (!editorRef.current) return;

    const selection = window.getSelection();
    if (selection.rangeCount === 0) return;

    const formats = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      strikeThrough: document.queryCommandState("strikeThrough"),
      justifyLeft: document.queryCommandState("justifyLeft"),
      justifyCenter: document.queryCommandState("justifyCenter"),
      justifyRight: document.queryCommandState("justifyRight"),
      insertUnorderedList: document.queryCommandState("insertUnorderedList"),
      insertOrderedList: document.queryCommandState("insertOrderedList"),
    };

    setActiveFormats(formats);
  }, []);

  // Enhanced format text function
  const formatText = useCallback(
    (command, value = null) => {
      try {
        // Focus the editor first
        editorRef.current?.focus();

        // Special handling for different commands
        switch (command) {
          case "createLink":
            if (value) {
              // Check if we have a selection
              const selection = window.getSelection();
              if (selection.rangeCount > 0 && !selection.isCollapsed) {
                document.execCommand(command, false, value);
              }
            }
            break;

          case "formatBlock":
            // Handle heading and block formatting
            const selection = window.getSelection();
            if (selection.rangeCount > 0) {
              const range = selection.getRangeAt(0);
              const container = range.commonAncestorContainer;
              const element =
                container.nodeType === Node.TEXT_NODE
                  ? container.parentElement
                  : container;

              // If we're in a heading, convert to normal text first
              if (element.tagName && element.tagName.match(/^H[1-6]$/)) {
                document.execCommand("formatBlock", false, "div");
              }

              // Apply new format
              if (value !== "div") {
                document.execCommand(command, false, value);
              }
            }
            break;

          case "insertUnorderedList":
          case "insertOrderedList":
            // Handle list formatting
            document.execCommand(command, false, value);
            break;

          default:
            document.execCommand(command, false, value);
        }

        // Update content
        if (editorRef.current) {
          onChange(editorRef.current.innerHTML);
        }

        // Update active formats
        setTimeout(updateActiveFormats, 10);
      } catch (error) {
        console.error("Format command failed:", error);
      }
    },
    [onChange, updateActiveFormats]
  );

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  const handleSelectionChange = useCallback(() => {
    updateActiveFormats();
  }, [updateActiveFormats]);

  const handleKeyDown = useCallback(
    (e) => {
      // Handle common keyboard shortcuts
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "b":
            e.preventDefault();
            formatText("bold");
            break;
          case "i":
            e.preventDefault();
            formatText("italic");
            break;
          case "u":
            e.preventDefault();
            formatText("underline");
            break;
          case "z":
            e.preventDefault();
            if (e.shiftKey) {
              formatText("redo");
            } else {
              formatText("undo");
            }
            break;
          case "y":
            e.preventDefault();
            formatText("redo");
            break;
        }
      }
    },
    [formatText]
  );

  // Setup event listeners
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    document.addEventListener("selectionchange", handleSelectionChange);
    editor.addEventListener("keydown", handleKeyDown);
    editor.addEventListener("mouseup", updateActiveFormats);
    editor.addEventListener("keyup", updateActiveFormats);

    return () => {
      document.removeEventListener("selectionchange", handleSelectionChange);
      editor.removeEventListener("keydown", handleKeyDown);
      editor.removeEventListener("mouseup", updateActiveFormats);
      editor.removeEventListener("keyup", updateActiveFormats);
    };
  }, [handleSelectionChange, handleKeyDown, updateActiveFormats]);

  const insertLink = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && !selection.isCollapsed) {
      const selectedText = selection.toString();
      if (selectedText) {
        setIsLinkModalOpen(true);
      } else {
        alert("Please select text first to create a link");
      }
    } else {
      alert("Please select text first to create a link");
    }
  };

  const applyLink = () => {
    if (linkUrl) {
      formatText("createLink", linkUrl);
      setLinkUrl("");
      setIsLinkModalOpen(false);
    }
  };

  const handleHeadingChange = (e) => {
    const value = e.target.value;
    if (value) {
      formatText("formatBlock", value);
    }
  };

  const getCurrentBlockFormat = () => {
    try {
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const container = range.commonAncestorContainer;
        const element =
          container.nodeType === Node.TEXT_NODE
            ? container.parentElement
            : container;

        // Check if we're in a heading
        if (element.tagName && element.tagName.match(/^H[1-6]$/)) {
          return element.tagName.toLowerCase();
        }

        // Check for other block elements
        if (element.tagName === "BLOCKQUOTE") return "blockquote";
        if (element.tagName === "PRE") return "pre";
      }
    } catch (error) {
      console.error("Error getting block format:", error);
    }
    return "";
  };

  const toolbarButtons = [
    { icon: Bold, command: "bold", title: "Bold (Ctrl+B)" },
    { icon: Italic, command: "italic", title: "Italic (Ctrl+I)" },
    { icon: Underline, command: "underline", title: "Underline (Ctrl+U)" },
    { icon: Strikethrough, command: "strikeThrough", title: "Strikethrough" },
  ];

  const alignmentButtons = [
    { icon: AlignLeft, command: "justifyLeft", title: "Align Left" },
    { icon: AlignCenter, command: "justifyCenter", title: "Align Center" },
    { icon: AlignRight, command: "justifyRight", title: "Align Right" },
  ];

  const listButtons = [
    { icon: List, command: "insertUnorderedList", title: "Bullet List" },
    { icon: ListOrdered, command: "insertOrderedList", title: "Numbered List" },
  ];

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden shadow-sm">
      {/* Toolbar */}
      <div className="bg-gray-50 border-b border-gray-200 p-3">
        <div className="flex flex-wrap items-center gap-1">
          {/* Text Formatting */}
          <div className="flex items-center gap-1">
            {toolbarButtons.map(({ icon: Icon, command, title }) => (
              <button
                key={command}
                type="button"
                onClick={() => formatText(command)}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                  activeFormats[command] ? "bg-purple-100 text-purple-700" : ""
                }`}
                title={title}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Alignment */}
          <div className="flex items-center gap-1">
            {alignmentButtons.map(({ icon: Icon, command, title }) => (
              <button
                key={command}
                type="button"
                onClick={() => formatText(command)}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                  activeFormats[command] ? "bg-purple-100 text-purple-700" : ""
                }`}
                title={title}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Lists */}
          <div className="flex items-center gap-1">
            {listButtons.map(({ icon: Icon, command, title }) => (
              <button
                key={command}
                type="button"
                onClick={() => formatText(command)}
                className={`p-2 rounded hover:bg-gray-200 transition-colors ${
                  activeFormats[command] ? "bg-purple-100 text-purple-700" : ""
                }`}
                title={title}
              >
                <Icon size={16} />
              </button>
            ))}
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Additional Tools */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={insertLink}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Insert Link"
            >
              <Link2 size={16} />
            </button>

            <button
              type="button"
              onClick={() => formatText("formatBlock", "blockquote")}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Quote"
            >
              <Quote size={16} />
            </button>

            <button
              type="button"
              onClick={() => formatText("formatBlock", "pre")}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Code Block"
            >
              <Code size={16} />
            </button>
          </div>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Heading Selector */}
          <select
            onChange={handleHeadingChange}
            className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 transition-colors"
            value={getCurrentBlockFormat()}
          >
            <option value="">Normal</option>
            <option value="h1">Heading 1</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="h4">Heading 4</option>
            <option value="h5">Heading 5</option>
            <option value="h6">Heading 6</option>
          </select>

          <div className="w-px h-6 bg-gray-300 mx-2"></div>

          {/* Undo/Redo */}
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => formatText("undo")}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Undo (Ctrl+Z)"
            >
              <Undo size={16} />
            </button>

            <button
              type="button"
              onClick={() => formatText("redo")}
              className="p-2 rounded hover:bg-gray-200 transition-colors"
              title="Redo (Ctrl+Y)"
            >
              <Redo size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        className="p-4 min-h-[400px] outline-none prose prose-sm max-w-none focus:ring-2 focus:ring-purple-500 focus:ring-inset"
        style={{
          whiteSpace: "pre-wrap",
          lineHeight: "1.6",
        }}
        onInput={handleInput}
        suppressContentEditableWarning={true}
        data-placeholder={placeholder}
      />

      {/* Link Modal */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Insert Link</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL (e.g., https://example.com)"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  applyLink();
                } else if (e.key === "Escape") {
                  setIsLinkModalOpen(false);
                  setLinkUrl("");
                }
              }}
            />
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => {
                  setIsLinkModalOpen(false);
                  setLinkUrl("");
                }}
              >
                Cancel
              </Button>
              <Button variant="primary" onClick={applyLink} disabled={!linkUrl}>
                Insert Link
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Custom Styles */}
      <style jsx>{`
        [contenteditable]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
        }

        [contenteditable] h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }

        [contenteditable] h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.83em 0;
        }

        [contenteditable] h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 1em 0;
        }

        [contenteditable] h4 {
          font-size: 1em;
          font-weight: bold;
          margin: 1.33em 0;
        }

        [contenteditable] h5 {
          font-size: 0.83em;
          font-weight: bold;
          margin: 1.67em 0;
        }

        [contenteditable] h6 {
          font-size: 0.67em;
          font-weight: bold;
          margin: 2.33em 0;
        }

        [contenteditable] blockquote {
          border-left: 4px solid #d1d5db;
          padding-left: 1rem;
          margin: 1rem 0;
          font-style: italic;
          color: #6b7280;
        }

        [contenteditable] pre {
          background-color: #f3f4f6;
          border: 1px solid #d1d5db;
          border-radius: 0.25rem;
          padding: 1rem;
          font-family: monospace;
          white-space: pre-wrap;
          margin: 1rem 0;
        }

        [contenteditable] ul,
        [contenteditable] ol {
          padding-left: 2rem;
          margin: 1rem 0;
        }

        [contenteditable] li {
          margin: 0.5rem 0;
        }

        [contenteditable] a {
          color: #7c3aed;
          text-decoration: underline;
        }

        [contenteditable] a:hover {
          color: #5b21b6;
        }
      `}</style>
    </div>
  );
}
